import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useBillArchives } from "@/hooks/useArchive";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, Legend } from "recharts";
import { Database, HardDrive, Clock, Shield, TrendingUp, Archive } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export function ArchiveAnalyticsDashboard({ workspaceId }: { workspaceId?: string }) {
  const { data: archives, isLoading } = useBillArchives(workspaceId);

  if (isLoading) {
    return <div className="text-muted-foreground">Loading analytics...</div>;
  }

  // Storage by commodity (mock data for demo)
  const storageData = [
    { name: 'Electricity', value: 450, color: 'hsl(var(--primary))' },
    { name: 'Water', value: 280, color: 'hsl(var(--secondary))' },
    { name: 'Gas', value: 320, color: 'hsl(var(--accent))' },
    { name: 'Waste', value: 150, color: 'hsl(var(--muted))' },
  ];

  // Archives by year (mock data)
  const archivesByYear = [
    { year: '2021', count: 234 },
    { year: '2022', count: 456 },
    { year: '2023', count: 678 },
    { year: '2024', count: 892 },
    { year: '2025', count: 145 },
  ];

  // Retrieval frequency (mock data)
  const retrievalData = [
    { month: 'Jan', count: 5 },
    { month: 'Feb', count: 8 },
    { month: 'Mar', count: 3 },
    { month: 'Apr', count: 12 },
    { month: 'May', count: 6 },
    { month: 'Jun', count: 9 },
  ];

  // Calculate stats
  const totalArchived = archives?.length || 0;
  const legalHoldCount = archives?.filter(a => a.legal_hold).length || 0;
  const totalStorageGB = archives?.reduce((acc, a) => acc + (a.archive_size_bytes || 0), 0) / (1024 * 1024 * 1024) || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Archive className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Total Archived</p>
            </div>
            <p className="text-2xl font-bold">{totalArchived.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Bills in archive</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Storage Used</p>
            </div>
            <p className="text-2xl font-bold">{totalStorageGB.toFixed(2)} GB</p>
            <p className="text-xs text-muted-foreground">Cold storage</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Legal Hold</p>
            </div>
            <p className="text-2xl font-bold">{legalHoldCount}</p>
            <p className="text-xs text-muted-foreground">Protected bills</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">Avg Retention</p>
            </div>
            <p className="text-2xl font-bold">7 years</p>
            <p className="text-xs text-muted-foreground">Regulatory compliance</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Storage by Commodity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Storage by Commodity
            </CardTitle>
            <CardDescription>Distribution of archived bills by utility type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={storageData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {storageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Archives by Year */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Archived Bills by Year
            </CardTitle>
            <CardDescription>Historical archive volume</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={archivesByYear}>
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Retrieval Frequency */}
        <Card>
          <CardHeader>
            <CardTitle>Archive Retrieval Frequency</CardTitle>
            <CardDescription>Number of restore requests per month</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={retrievalData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" name="Retrievals" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Upcoming Retention Expirations */}
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Retention Expirations</CardTitle>
            <CardDescription>Bills eligible for permanent deletion</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {archives?.slice(0, 5).filter(a => a.retention_expires_at).map((archive) => (
                <div key={archive.id} className="flex items-center justify-between border-b pb-2">
                  <div>
                    <p className="text-sm font-medium">Bill #{archive.bill_id?.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">
                      {archive.retention_period_years} year retention
                    </p>
                  </div>
                  <div className="text-right">
                    {archive.legal_hold ? (
                      <Badge variant="destructive">Legal Hold</Badge>
                    ) : (
                      <Badge variant="secondary">
                        {archive.retention_expires_at && 
                          formatDistanceToNow(new Date(archive.retention_expires_at), { addSuffix: true })}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
              {(!archives || archives.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No upcoming expirations
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Retention Compliance Report */}
      <Card>
        <CardHeader>
          <CardTitle>Retention Compliance Summary</CardTitle>
          <CardDescription>Overview of retention policy adherence</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">98.5%</p>
                <p className="text-sm text-muted-foreground">Compliance Rate</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">45</p>
                <p className="text-sm text-muted-foreground">Days Avg Retrieval</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">12</p>
                <p className="text-sm text-muted-foreground">Audits This Year</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
